import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import Logo from './Logo';
import { IconCode } from './Icons';
import DiscordLoginButton from './DiscordLoginButton';
import './Navbar.css';

/* ─────────────────────────────────────────────
   NAV config – simple links + items with
   dropdown: [ { to, label, desc } ]
───────────────────────────────────────────── */
const NAV = [
  { to: '/home',  label: 'Home' },
  { to: '/about', label: 'About' },
  {
    label: 'Services',
    to: '/services',
    dropdown: [
      { to: '/services',       label: 'All Services',     desc: 'Overview of everything we offer' },
      { to: '/contact',        label: 'Recommendations',  desc: 'Suggest a bot or new developer tool' },
      { to: '/support-server', label: 'Support Server',   desc: 'Get help on Discord' },
    ],
  },
  {
    label: 'Products',
    to: '/bots',
    dropdown: [
      {
        to: '/bots',
        label: 'All Products',
        desc: 'Browse the full PERM Systems bot library',
        icon: null,
      },
      {
        to: '/code-assist/home',
        label: 'Code Assist',
        desc: 'AI-powered coding assistant for Discord',
        icon: IconCode,
        badge: 'Live',
      },
      {
        to: '/code-assist/use/home',
        label: 'Use Code Assist',
        desc: 'AI coding chat — debug, explain, generate code',
        icon: IconCode,
        badge: 'Web',
      },
    ],
  },
  { to: '/contact', label: 'Contact' },
  { to: '/docs',    label: 'Docs' },
  { to: '/blog',    label: 'Blog' },
];

function DropdownMenu({ items, visible }) {
  return (
    <div className={`nav-dropdown${visible ? ' nav-dropdown--visible' : ''}`} role="menu">
      <div className="nav-dropdown__inner">
        {items.map(({ to, label, desc, icon: Icon, badge }) => (
          <NavLink key={to} to={to} className="nav-dropdown__item" role="menuitem">
            {Icon && (
              <div className="nav-dropdown__item-icon">
                <Icon size={18} color="var(--blue-dark)" />
              </div>
            )}
            <div className="nav-dropdown__item-text">
              <span className="nav-dropdown__item-label">
                {label}
                {badge && <span className="nav-dropdown__badge">{badge}</span>}
              </span>
              {desc && <span className="nav-dropdown__item-desc">{desc}</span>}
            </div>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [activeDD,  setActiveDD]  = useState(null); // label of open dropdown
  const timerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openDD  = (label) => { clearTimeout(timerRef.current); setActiveDD(label); };
  const closeDD = ()      => { timerRef.current = setTimeout(() => setActiveDD(null), 120); };
  const close   = ()      => setMenuOpen(false);

  return (
    <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
      <div className="navbar__inner">

        {/* Logo + wordmark */}
        <Link to="/home" className="navbar__logo" onClick={close} aria-label="PERM Systems home">
          <Logo size={32} />
          <span className="navbar__wordmark">
            <span className="navbar__wordmark-p">p</span>erm systems
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="navbar__links" aria-label="Main navigation">
          {NAV.map((item) => {
            if (item.dropdown) {
              const isOpen = activeDD === item.label;
              return (
                <div
                  key={item.label}
                  className="nav-item nav-item--dd"
                  onMouseEnter={() => openDD(item.label)}
                  onMouseLeave={closeDD}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `navbar__link nav-item__trigger${isActive ? ' navbar__link--active' : ''}${isOpen ? ' navbar__link--open' : ''}`
                    }
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                  >
                    {item.label}
                    <svg className="nav-caret" width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">
                      <path d="M1 1l4.5 4.5L10 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </NavLink>
                  <DropdownMenu items={item.dropdown} visible={isOpen} />
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `navbar__link${isActive ? ' navbar__link--active' : ''}`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="navbar__cta">
          <DiscordLoginButton size="sm" label="Log in with Discord" />
          <Link to="/bots" className="btn btn--primary btn--sm">Explore Products</Link>
        </div>

        {/* Hamburger */}
        <button
          className={`navbar__hamburger${menuOpen ? ' open' : ''}`}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(v => !v)}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`navbar__mobile${menuOpen ? ' navbar__mobile--open' : ''}`} aria-hidden={!menuOpen}>
        {NAV.map((item) =>
          item.dropdown ? (
            <div key={item.label} className="navbar__mobile-group">
              <p className="navbar__mobile-group-label">{item.label}</p>
              {item.dropdown.map(({ to, label }) => (
                <NavLink key={to} to={to}
                  className={({ isActive }) => `navbar__mobile-link navbar__mobile-link--sub${isActive ? ' active' : ''}`}
                  onClick={close}>
                  {label}
                </NavLink>
              ))}
            </div>
          ) : (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) => `navbar__mobile-link${isActive ? ' active' : ''}`}
              onClick={close}>
              {item.label}
            </NavLink>
          )
        )}
        <div className="navbar__mobile-actions">
          <DiscordLoginButton size="sm" label="Log in with Discord" />
          <Link to="/bots" className="btn btn--primary" onClick={close}>Explore Products</Link>
        </div>
      </div>
    </header>
  );
}
